/* Phase 4 + S21 v3 · jp-product.js — PDP enhancements on top of Dawn machinery.
   Gallery arrows + presentation-only product stage tone, qty stepper, size-guide
   dialog, sticky buy bar, share, ARIA tabs, cost-per-cut teaser (Intl),
   ATC live price, compact state mirror. Persona reactions (tuned note,
   rec chips, hints, pick badge) are pure CSS off body.persona-* — no JS.
   All binds idempotent (data flags) + re-run on shopify:section:load. */
(() => {
  if (window.__jpProductInit) return;
  window.__jpProductInit = true;

  const ready = (fn) =>
    document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn);
  let activeProductCleanup = null;
  let activeProductInfo = null;
  let activeProductControllers = [];
  let activeProductObservers = [];
  const trackProductController = (controller) => {
    activeProductControllers.push(controller);
    return controller;
  };
  const abortProductControllers = () => {
    activeProductControllers.forEach((controller) => {
      controller.__jpDispose?.();
      controller.abort();
    });
    activeProductControllers = [];
  };
  const trackProductObserver = (observer) => {
    activeProductObservers.push(observer);
    return observer;
  };
  const disconnectProductObservers = () => {
    activeProductObservers.forEach((observer) => observer.disconnect());
    activeProductObservers = [];
  };

  /* ---- money: format minor-unit cents via Intl (pattern: jp-cost-per-cut).
          Currency default digits respected; only force 3 when sub-minor-unit. ---- */
  const fmtMoney = (cents, currency) => {
    const lang = document.documentElement.lang || 'en';
    const value = cents / 100;
    const opts = { style: 'currency', currency };
    if (value > 0 && value < 0.01) { opts.minimumFractionDigits = 3; opts.maximumFractionDigits = 3; }
    try {
      return new Intl.NumberFormat(lang, opts).format(value);
    } catch { return value.toFixed(2) + ' ' + currency; }
  };

  /* ---- cost-per-cut teaser: cents ÷ (cuts × days × years).
          Server renders it [hidden]; reveal only once a real figure exists
          (Dawn 15 has no no-js class — this is the no-JS guard). ---- */
  const renderPercut = () => {
    document.querySelectorAll('[data-jp-percut]').forEach((el) => {
      const out = el.querySelector('[data-jp-percut-val]');
      if (!out) return;
      const cents = parseFloat(el.dataset.priceCents);
      const cuts = (parseFloat(el.dataset.cuts) || 8) * (parseFloat(el.dataset.days) || 312) * (parseFloat(el.dataset.years) || 10);
      if (!cents || !cuts || !el.dataset.currency) return;
      const value = fmtMoney(cents / cuts, el.dataset.currency);
      /* This runs from the price-block observer after a variant swap. Writing
         the same text unconditionally creates a fresh childList mutation and
         can starve the main thread in an endless observer/microtask loop. */
      if (out.textContent !== value) out.textContent = value;
      if (el.hidden) el.hidden = false;
    });
  };

  /* ---- ATC label: Kit/Reserve swap + optional live price suffix ---- */
  const atcLabel = () => {
    const atcBtn = document.querySelector('[data-jp-atc]');
    if (!atcBtn) return;
    const span = atcBtn.querySelector('span');
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    const priceNow = () => document.querySelector('[id^="price-"] .jp-pp__now');
    const withPrice = (label) => {
      if (!atcBtn.hasAttribute('data-jp-atc-price')) return label;
      const now = priceNow();
      return now ? label + ' — ' + now.textContent.trim() : label;
    };
    const current = () => {
      const pre = stockBlock && stockBlock.querySelector('.jp-pp__stock--pre');
      return withPrice(pre ? atcBtn.dataset.labelReserve : atcBtn.dataset.labelKit);
    };
    const sync = () => {
      /* Dawn's toggleSubmitButton writes window.variantStrings.addToCart on
         enable — keep that global equal to our full label so Dawn writes
         the brand text (incl. price) itself. */
      const label = current();
      if (window.variantStrings) window.variantStrings.addToCart = label;
      if (span && !atcBtn.hasAttribute('disabled')) span.textContent = label;
    };
    sync();
    if (stockBlock && !stockBlock.dataset.jpAtcBound) {
      stockBlock.dataset.jpAtcBound = '1';
      trackProductObserver(new MutationObserver(sync)).observe(stockBlock, { childList: true, subtree: true });
    }
    return sync;
  };

  /* ---- C8 · dispatch date (a DATE, never a countdown) ----
     Compute the honest dispatch/arrival line from the store clock (cutoff + IANA tz) + the C1 transit window
     (single settings source, so it matches the shippingDetails schema). This element exists ONLY in the
     in-stock branch, so it always shows the cutoff-derived estimate; its server-rendered no-JS fallback is
     the neutral next-business-day line, never a back-order window. Delayed promises (dispatch window,
     forged-to-order lead times) belong to the 'pre' inventory branch only — a positive-stock variant, left-
     handed included, is never presented as delayed (2026-07-28). Recomputes when Dawn re-renders the
     inventory block on variant change, minute boundaries and visibility restoration. The rendered-text
     cache makes DOM writes idempotent so the observer cannot loop. */
  const bizAdd = (date, n) => {
    let added = 0;
    while (added < n) {
      date.setUTCDate(date.getUTCDate() + 1);
      const wd = date.getUTCDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return date;
  };
  const applyDispatch = () => {
    const el = document.querySelector('[data-jp-dispatch]');
    if (!el) return;
    const out = el.querySelector('[data-jp-dispatch-txt]') || el;
    const d = el.dataset;
    const lang = document.documentElement.lang || 'en-GB';
    try {
      const tz = d.tz || 'Europe/London';
      const transit = (value, fallback) => value == null || value === '' ? fallback
        : /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
      const tmin = transit(d.transitMin, 2);
      const tmax = transit(d.transitMax, 4);
      if (tmin == null || tmax == null || tmin > tmax || tmax > 365) return;
      if (d.cutoff && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(d.cutoff)) return;
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(new Date());
      const gp = (t) => parseInt((parts.find((p) => p.type === t) || {}).value, 10);
      let hh = gp('hour'); if (hh === 24) hh = 0;
      const mi = gp('minute');
      const dispatch = new Date(Date.UTC(gp('year'), gp('month') - 1, gp('day')));
      const wd = dispatch.getUTCDay();
      const isWeekday = wd !== 0 && wd !== 6;
      const cut = (d.cutoff || '14:00').split(':');
      const ch = parseInt(cut[0], 10);
      const cm = parseInt(cut[1], 10) || 0;
      /* the cutoff is the same-day-dispatch promise ("order by 2pm → ships today"); exclusive at the exact
         minute so 14:00 counts as after a 14:00 cutoff. Handling days are a SCHEMA concept (jp-schema-product
         handlingTime), deliberately NOT part of this on-page promise. */
      const beforeCutoff = hh < ch || (hh === ch && mi < cm);
      const today = isWeekday && beforeCutoff;
      if (!today) bizAdd(dispatch, 1); /* next business day */
      const arrMin = bizAdd(new Date(dispatch), tmin);
      const arrMax = bizAdd(new Date(dispatch), tmax);
      const wdName = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'long' }).format(dispatch);
      const fmtD = (dt) => new Intl.DateTimeFormat(lang, { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(dt);
      const dispatchText = today
        ? (d.tmplToday || 'Dispatches today').replace('[cutoff]', d.cutoff || '')
        : (d.tmplDay || 'Dispatches [day]').replace('[day]', wdName);
      const arrivesText = (d.tmplArrives || 'Arrives [start]–[end]').replace('[start]', fmtD(arrMin)).replace('[end]', fmtD(arrMax));
      const renderKey = JSON.stringify([dispatchText, arrivesText]);
      if (el.dataset.jpDispatchDone === renderKey) return;
      el.dataset.jpDispatchDone = renderKey;
      out.textContent = '';
      const b = document.createElement('b');
      b.textContent = dispatchText;
      const sp = document.createElement('span');
      sp.className = 'jp-pp__dispatch-arr';
      sp.textContent = ' · ' + arrivesText;
      out.append(b, sp);
    } catch (e) { /* leave the server fallback in place */ }
  };
  let dispatchTimer = 0;
  const scheduleDispatch = () => {
    clearTimeout(dispatchTimer);
    if (document.hidden || !document.querySelector('[data-jp-dispatch]')) return;
    dispatchTimer = setTimeout(() => {
      applyDispatch();
      scheduleDispatch();
    }, 60000 - Date.now() % 60000 + 25);
  };
  document.addEventListener('visibilitychange', () => { applyDispatch(); scheduleDispatch(); });
  window.addEventListener('pagehide', () => clearTimeout(dispatchTimer));
  window.addEventListener('pageshow', () => { applyDispatch(); scheduleDispatch(); });
  const dispatchDate = () => {
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    applyDispatch();
    scheduleDispatch();
    if (stockBlock && !stockBlock.dataset.jpDispatchBound) {
      stockBlock.dataset.jpDispatchBound = '1';
      trackProductObserver(new MutationObserver(() => { applyDispatch(); scheduleDispatch(); })).observe(stockBlock, { childList: true, subtree: true });
    }
  };

  /* ---- A6 · student steer mini-card — 30-day dismiss + click/dismiss events ---- */
  const steerCard = () => {
    const card = document.querySelector('[data-jp-steer]');
    if (!card) return;
    const handle = card.dataset.handle || '';
    const key = 'jp_steer_dismissed_' + handle;
    const DAYS30 = 30 * 24 * 60 * 60 * 1000;
    /* re-hide runs UNCONDITIONALLY (before the bind guard) so a re-rendered card re-applies the dismiss state */
    try {
      const privacy = window.JapanScissors && window.JapanScissors.privacy;
      const stored = (privacy && privacy.getPreference(key)) || '';
      const ts = Number(stored);
      const now = Date.now();
      const clockSkew = 5 * 60 * 1000;
      if (!Number.isFinite(ts) || ts <= 0 || ts > now + clockSkew) {
        if (stored && privacy) privacy.removePreference(key);
        card.hidden = false;
      } else if (now - ts < DAYS30) {
        card.hidden = true;
      } else {
        if (privacy) privacy.removePreference(key);
        card.hidden = false;
      }
    } catch (e) { card.hidden = false; }
    if (card.dataset.jpSteerBound) return;
    card.dataset.jpSteerBound = '1';
    const link = card.querySelector('[data-jp-steer-link]');
    if (link) link.addEventListener('click', () => {
      try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_steer_click', handle: handle } })); } catch (e) { /* no-op */ }
    });
    const dismiss = card.querySelector('[data-jp-steer-dismiss]');
    if (dismiss) dismiss.addEventListener('click', () => {
      const privacy = window.JapanScissors && window.JapanScissors.privacy;
      if (privacy) privacy.setPreference(key, String(Date.now()));
      card.hidden = true;
      try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_steer_dismiss', handle: handle } })); } catch (e) { /* no-op */ }
    });
  };

  const init = () => {
    const currentProductInfo = document.querySelector('product-info[id^="MainProduct"]');
    if (currentProductInfo) activeProductInfo = currentProductInfo;

    /* ---- gallery prev/next + stage tone ---- */
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      /* The CSS :not(:defined) rail is the complete partial-asset fallback.
         Do not attach controls whose callbacks require Dawn's missing method. */
      if (typeof gallery.setActiveMedia !== 'function') return;
      const jpThumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      const lis = () => [...gallery.querySelectorAll('.jp-pp__viewer li')];
      const stage = gallery.querySelector('[data-jp-stage]');
      const syncTone = () => {
        if (!stage) return;
        const active = lis().find((li) => li.classList.contains('is-active'));
        if (!active || !active.dataset.jpTone) return;
        const dark = active.dataset.jpTone === 'dark';
        if (stage.classList.contains('is-dark') === dark) return;
        /* Both supported stage tones currently use the same white product
           canvas. Keep the semantic class in sync without fading the active
           image to blank for a background change the shopper cannot see. */
        stage.classList.toggle('is-dark', dark);
      };
      const syncVideoDetails = () => {
        const activeMediaId = lis().find((li) => li.classList.contains('is-active'))?.dataset.mediaId || '';
        gallery.querySelectorAll('[data-jp-video-details]').forEach((details) => {
          details.hidden = !activeMediaId || details.dataset.mediaId !== activeMediaId;
        });
      };
      if (!gallery.dataset.jpGalBound) {
        gallery.dataset.jpGalBound = '1';
        /* Dawn may prepend a selected variant's featured media. Preserve the
           server-rendered order so arrows and dots keep the same sequence. */
        const gItems = lis();
        const step = (dir) => {
          const activeId = lis().find((li) => li.classList.contains('is-active'))?.dataset.mediaId;
          const current = Math.max(0, gItems.findIndex((li) => li.dataset.mediaId === activeId));
          const next = (current + dir + gItems.length) % gItems.length;
          gallery.setActiveMedia(gItems[next].dataset.mediaId, false);
        };
        gallery.querySelector('[data-jp-gallery-prev]')?.addEventListener('click', () => step(-1));
        gallery.querySelector('[data-jp-gallery-next]')?.addEventListener('click', () => step(1));

        const viewer = gallery.querySelector('.jp-pp__viewer');
        /* P3.2 · dot pagination + horizontal swipe — only for multi-image products. The dots
           remain fully operable controls on mobile, where the thumbnail rail is hidden. Reuse the
           translated thumbnail labels so every visible control has an accessible name. */
        let syncDots = () => {};
        if (stage && gItems.length > 1) {
          const dots = document.createElement('div');
          dots.className = 'jp-pp__dots';
          dots.setAttribute('role', 'group');
          dots.setAttribute('aria-label', gallery.getAttribute('aria-label') || 'Gallery');
          gItems.forEach((li, index) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'jp-pp__gdot';
            const thumbButton = gallery.querySelector(`[data-target="${li.dataset.mediaId}"] button`);
            b.setAttribute('aria-label', thumbButton?.getAttribute('aria-label') || `View gallery item ${index + 1}`);
            if (li.id) b.setAttribute('aria-controls', li.id);
            b.addEventListener('click', () => gallery.setActiveMedia(li.dataset.mediaId, false));
            dots.appendChild(b);
          });
          stage.appendChild(dots);
          syncDots = () => {
            /* Dawn prepends a selected variant's image. Keep dot order tied to
               the canonical server order instead of that mutable DOM order. */
            const activeId = lis().find((li) => li.classList.contains('is-active'))?.dataset.mediaId;
            const cur = gItems.findIndex((li) => li.dataset.mediaId === activeId);
            Array.prototype.forEach.call(dots.children, (d, i) => {
              const active = i === cur;
              d.classList.toggle('is-on', active);
              if (active) d.setAttribute('aria-current', 'true');
              else d.removeAttribute('aria-current');
            });
          };
          syncDots();
          if (viewer && 'PointerEvent' in window) {
            let sx = 0, sy = 0, tracking = false;
            viewer.addEventListener('pointerdown', (e) => {
              if (e.pointerType === 'mouse' && e.button !== 0) return;
              sx = e.clientX; sy = e.clientY; tracking = true;
            });
            viewer.addEventListener('pointerup', (e) => {
              if (!tracking) return;
              tracking = false;
              const dx = e.clientX - sx, dy = e.clientY - sy;
              /* horizontal fling only — leaves taps (→ zoom) and vertical scrolls alone */
              if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
            });
            viewer.addEventListener('pointercancel', () => { tracking = false; });
          }
        }
        /* media-gallery toggles is-active on the lis (thumb clicks + variant media changes)
           — observe class flips (never patch Dawn), keep tone + dots in step */
        const liveRegion = gallery.querySelector('[id^="GalleryStatus"]');
        let announcedMediaId = lis().find((li) => li.classList.contains('is-active'))?.dataset.mediaId || '';
        const syncGalleryA11y = () => {
          const items = lis();
          const index = items.findIndex((li) => li.classList.contains('is-active'));
          if (index < 0) return;
          const mediaId = items[index].dataset.mediaId;
          if (!mediaId || mediaId === announcedMediaId) return;
          announcedMediaId = mediaId;
          const activeThumb = gallery.querySelector(`[data-target="${mediaId}"]`);
          const position = activeThumb?.dataset.mediaPosition || String(index + 1);
          if (liveRegion) {
            const template = window.accessibilityStrings?.imageAvailable;
            liveRegion.textContent = template ? template.replace('[index]', position) : `${position} / ${items.length}`;
          }
          if (activeThumb && jpThumbs) {
            const horizontal = jpThumbs.scrollWidth > jpThumbs.clientWidth;
            const overflows = horizontal
              ? jpThumbs.scrollWidth > jpThumbs.clientWidth + 1
              : jpThumbs.scrollHeight > jpThumbs.clientHeight + 1;
            const railRect = jpThumbs.getBoundingClientRect();
            const thumbRect = activeThumb.getBoundingClientRect();
            const edgeTolerance = 2;
            const isVisible = horizontal
              ? thumbRect.left >= railRect.left - edgeTolerance && thumbRect.right <= railRect.right + edgeTolerance
              : thumbRect.top >= railRect.top - edgeTolerance && thumbRect.bottom <= railRect.bottom + edgeTolerance;

            /* JP EDIT — leave the thumbnail rail still when the selected item is already
               visible. Recenter only an off-screen item, preserving access to variant media
               without adding a second, unnecessary motion after every gallery change. */
            if (overflows && !isVisible) {
              jpThumbs.scrollTo({
                left: horizontal ? Math.max(0, activeThumb.offsetLeft - (jpThumbs.clientWidth - activeThumb.offsetWidth) / 2) : jpThumbs.scrollLeft,
                top: horizontal ? jpThumbs.scrollTop : Math.max(0, activeThumb.offsetTop - (jpThumbs.clientHeight - activeThumb.offsetHeight) / 2),
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              });
            }
          }
        };
        if (viewer) trackProductObserver(new MutationObserver(() => { syncTone(); syncDots(); syncGalleryA11y(); syncVideoDetails(); })).observe(viewer, { attributes: true, attributeFilter: ['class'], subtree: true });
        syncTone();
        syncVideoDetails();
      }
    });

    /* ---- qty stepper (document-level, bind once) ---- */
    /* reflect the lower bound on the minus button so at qty=min it reads as inert, not a silent no-op */
    const applyQtyRules = (inp) => {
      if (!inp) return;
      const cartQuantity = parseInt(inp.dataset.cartQuantity || '0', 10) || 0;
      const ruleMin = parseInt(inp.dataset.min || '1', 10) || 1;
      const ruleMax = inp.dataset.max == null ? null : parseInt(inp.dataset.max, 10);
      const step = parseInt(inp.step || '1', 10) || 1;
      const remainingMax = Number.isFinite(ruleMax) ? Math.max(0, ruleMax - cartQuantity) : null;
      let effectiveMin = ruleMin;
      if (cartQuantity >= ruleMin) effectiveMin = Math.min(effectiveMin, step);
      if (remainingMax !== null) effectiveMin = Math.min(effectiveMin, remainingMax);
      inp.min = effectiveMin;
      if (remainingMax !== null) inp.max = remainingMax;
      else inp.removeAttribute('max');
      inp.value = effectiveMin;
    };
    const syncQtyMin = (wrap) => {
      const inp = wrap?.querySelector('input');
      const minus = wrap?.querySelector('[data-jp-qty="-1"]');
      if (!inp || !minus) return;
      const value = parseInt(inp.value, 10) || parseInt(inp.min, 10) || 1;
      const min = parseInt(inp.min, 10) || 1;
      const max = inp.max === '' ? null : parseInt(inp.max, 10);
      const atMin = value <= min;
      minus.disabled = atMin;
      minus.setAttribute('aria-disabled', atMin ? 'true' : 'false');
      const plus = wrap?.querySelector('[data-jp-qty="1"]');
      const atMax = Number.isFinite(max) && value >= max;
      if (plus) {
        plus.disabled = atMax;
        plus.setAttribute('aria-disabled', atMax ? 'true' : 'false');
      }
    };
    document.querySelectorAll('.jp-pp__qty').forEach((wrap) => {
      const inp = wrap.querySelector('input');
      if (inp && !inp.dataset.jpRulesBound) {
        inp.dataset.jpRulesBound = '1';
        let queued = false;
        trackProductObserver(new MutationObserver(() => {
          if (queued) return;
          queued = true;
          queueMicrotask(() => {
            queued = false;
            applyQtyRules(inp);
            syncQtyMin(wrap);
          });
        })).observe(inp, { attributes: true, attributeFilter: ['data-cart-quantity', 'data-min', 'data-max', 'step'] });
      }
      applyQtyRules(inp);
      syncQtyMin(wrap);
    });
    if (!document.body.dataset.jpQtyBound) {
      document.body.dataset.jpQtyBound = '1';
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-jp-qty]');
        if (!btn) return;
        const wrap = btn.closest('.jp-pp__qty');
        const input = wrap?.querySelector('input');
        if (!input) return;
        const min = parseInt(input.min || '1', 10) || 1;
        const max = input.max === '' ? null : parseInt(input.max, 10);
        const step = parseInt(input.step || '1', 10) || 1;
        const direction = parseInt(btn.dataset.jpQty, 10) < 0 ? -1 : 1;
        let next = (parseInt(input.value, 10) || min) + direction * step;
        next = Math.max(min, next);
        if (Number.isFinite(max)) next = Math.min(max, next);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncQtyMin(wrap);
      });
      document.addEventListener('change', (e) => {
        const inp = e.target.closest('.jp-pp__qty input');
        if (inp) {
          const min = parseInt(inp.min || '1', 10) || 1;
          const max = inp.max === '' ? null : parseInt(inp.max, 10);
          const step = parseInt(inp.step || '1', 10) || 1;
          let value = parseInt(inp.value, 10) || min;
          value = min + Math.max(0, Math.round((value - min) / step)) * step;
          if (Number.isFinite(max)) value = Math.min(max, value);
          inp.value = Math.max(min, value);
          syncQtyMin(inp.closest('.jp-pp__qty'));
        }
      });
    }

    /* size-guide JUMP links are replaced with variant-selects. Delegate to the
       product owner so every current anchor retains focus/motion handling.
       The ordinary anchor remains the no-JS and missing-target fallback. */
    const pageGuide = document.getElementById('size-guide');
    const pageGuideOwner = pageGuide?.closest('product-info');
    if (pageGuide && pageGuideOwner && !pageGuide.dataset.jpJumpBound) {
      pageGuide.dataset.jpJumpBound = '1';
      const jumpController = trackProductController(new AbortController());
      jumpController.__jpDispose = () => { delete pageGuide.dataset.jpJumpBound; };
      pageGuideOwner.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-jp-guide-jump]');
        if (!trigger || !pageGuide.isConnected || !pageGuideOwner.contains(trigger)
          || trigger.getAttribute('href') !== '#size-guide'
          || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        pageGuide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (!pageGuide.hasAttribute('tabindex')) pageGuide.setAttribute('tabindex', '-1');
        pageGuide.focus({ preventScroll: true });
        /* S33 §2b · analytics — the jump link is the on-page size-guide's open path (same signal as the modal). Non-blocking. */
        try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_size_guide_opened' } })); } catch (err) { /* no-op */ }
      }, { signal: jumpController.signal });
    }

    /* ---- size guide dialog ---- */
    const guide = document.querySelector('[data-jp-guide]');
    const guideOwner = guide?.closest('product-info') || document.body;
    const decorateGuideOpeners = () => {
      if (!guide?.isConnected) return;
      guideOwner.querySelectorAll('[data-jp-guide-open]').forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        if (guide.id) trigger.setAttribute('aria-controls', guide.id);
      });
    };
    decorateGuideOpeners();
    if (guide && !guide.dataset.jpBound) {
      guide.dataset.jpBound = '1';
      guide.classList.add('is-enhanced');
      guide.setAttribute('role', 'dialog');
      guide.setAttribute('aria-modal', 'true');
      guide.hidden = true;
      const guideController = trackProductController(new AbortController());
      const guideSignal = guideController.signal;
      let lastFocus = null;
      /* isolate the background from AT swipe-nav while the guide is open (mirrors the zoom lightbox).
         The guide is NOT portaled to <body> — it lives inside <product-info> — so a flat "body > *"
         sweep would leave the whole PDP inside <product-info> interactive. Instead we walk from the
         guide up to <body>, inerting the SIBLINGS at every ancestor level (never an ancestor itself,
         which contains the guide). We record exactly which elements we set (skipping the scrim and any
         node already inert, e.g. by an open jp-layer or the zoom) and clear only those — so this never
         fights jp-layers' own inert bookkeeping and cleanly handles the nested-dialog case. */
      let guideInerted = [];
      const inertBg = (on) => {
        if (on) {
          guideInerted = [];
          let node = guide;
          while (node && node !== document.body && node.parentElement) {
            Array.prototype.forEach.call(node.parentElement.children, (sib) => {
              if (sib === node || sib.id === 'jp-scrim' || sib.inert) return;
              const tag = sib.tagName;
              if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE') return;
              sib.inert = true;
              guideInerted.push(sib);
            });
            node = node.parentElement;
          }
        } else {
          guideInerted.forEach((el) => { el.inert = false; });
          guideInerted = [];
        }
      };
      let hideTimer = 0;
      let guideOpenFrame = 0;
      let guideOpen = false;
      const guideScrollKey = 'pdp-size-guide';
      const open = (event, trigger) => {
        event?.preventDefault();
        if (guideOpen) return;
        guideOpen = true;
        lastFocus = trigger || document.activeElement;
        clearTimeout(hideTimer); // cancel a pending hide from a just-fired close() so guide.hidden and .is-open never disagree
        guide.hidden = false;
        guideOpenFrame = requestAnimationFrame(() => {
          if (!guideOpen || !guide.isConnected) return;
          guide.classList.add('is-open');
          // The dialog is display:none until this class lands. Focus only once
          // the owning opening frame has made its Close control renderable.
          guide.querySelector('.jp-guide__close')?.focus({ preventScroll: true });
        });
        inertBg(true); // before focus moves into the dialog
        if (window.JapanScissors?.layers?.lockScroll) window.JapanScissors.layers.lockScroll(guideScrollKey);
        else document.body.classList.add('no-scroll');
        /* S33 §2b · analytics — size-guide modal opened (an engagement signal on the PDP). Non-blocking. */
        try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_size_guide_opened' } })); } catch (e) { /* no-op */ }
      };
      const close = () => {
        if (!guideOpen) return;
        guideOpen = false;
        cancelAnimationFrame(guideOpenFrame);
        guide.classList.remove('is-open');
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(guideScrollKey);
        else document.body.classList.remove('no-scroll');
        inertBg(false); // restore the background before focus returns to the opener
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => { guide.hidden = true; }, 300);
        // Dawn keeps its retired selector connected but display:none for
        // 500ms. A connected anchor is not necessarily a usable focus target.
        const canReturnFocus = (trigger) => trigger?.isConnected
          && guideOwner.contains(trigger) && trigger.getClientRects().length > 0;
        const returnFocus = canReturnFocus(lastFocus) ? lastFocus
          : [...guideOwner.querySelectorAll('[data-jp-guide-open]')].find(canReturnFocus);
        returnFocus?.focus({ preventScroll: true });
      };
      guideController.__jpDispose = () => {
        guideOpen = false;
        cancelAnimationFrame(guideOpenFrame);
        clearTimeout(hideTimer);
        if (guide.hidden) return;
        guide.classList.remove('is-open');
        guide.hidden = true;
        inertBg(false);
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(guideScrollKey);
        else document.body.classList.remove('no-scroll');
      };
      /* Dawn replaces variant-selects (including its guide link) without
         replacing this dialog or running init(). Delegate to its product owner
         and restore ARIA on fresh controls before they receive keyboard focus. */
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-jp-guide-open]');
        if (!trigger || !guide.isConnected || !guideOwner.contains(trigger)) return;
        open(event, trigger);
      }, { signal: guideSignal });
      trackProductObserver(new MutationObserver(() => {
        if (!guideSignal.aborted) decorateGuideOpeners();
      })).observe(guideOwner, { childList: true, subtree: true });
      guide.querySelectorAll('[data-jp-guide-close]').forEach((b) => b.addEventListener('click', close, { signal: guideSignal }));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !guide.hidden) close();
      }, { signal: guideSignal });
      /* Tab focus trap — the dialog sets aria-modal="true"; keep focus inside it */
      guide.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || guide.hidden) return;
        const f = [...guide.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }, { signal: guideSignal });
      document.addEventListener('jp:layers:reset', guideController.__jpDispose, { signal: guideSignal });
      window.addEventListener('pagehide', guideController.__jpDispose, { signal: guideSignal });
    }

    /* ---- image zoom / lightbox (premium close-up for blade/finish detail) ---- */
    if (!document.body.dataset.jpZoomBound) {
      document.body.dataset.jpZoomBound = '1';
      let zoomReturn = null;
      const z = document.createElement('div');
      z.className = 'jp-zoom';
      z.setAttribute('role', 'dialog');
      z.setAttribute('aria-modal', 'true');
      z.hidden = true;
      z.innerHTML =
        '<div class="jp-zoom__scrim" data-jp-zoom-close></div>' +
        '<button type="button" class="jp-zoom__close" data-jp-zoom-close><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"></path></svg></button>' +
        '<img class="jp-zoom__img" alt="">';
      document.body.appendChild(z);
      // localized close label (en/fr/it) carried on the stage element, falls back to English
      const jpStageEl = document.querySelector('[data-jp-stage]');
      z.setAttribute('aria-label', (jpStageEl && jpStageEl.dataset.zoomDialogLabel) || 'Full-size product image');
      z.querySelector('.jp-zoom__close').setAttribute('aria-label', (jpStageEl && jpStageEl.dataset.closeLabel) || 'Close');
      const zImg = z.querySelector('.jp-zoom__img');
      zImg.draggable = false;
      // touch-action inline (not only in the async-deferred stylesheet) so the browser never hijacks the first pinch/pan
      zImg.style.touchAction = 'none'; zImg.style.userSelect = 'none'; z.style.touchAction = 'none';

      /* ---- pinch-zoom + pan (S7): two-finger pinch, drag-pan when zoomed, dbl-tap/dbl-click/wheel
         to toggle. Tapping the image no longer dismisses — close is X · scrim · Esc only. ---- */
      const MAX_SCALE = 4, DBL_SCALE = 2.4;
      const reduceMo = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let zs = 1, ztx = 0, zty = 0;   // live transform: scale + translate(px)
      let C0x = 0, C0y = 0;           // image centre in screen coords at identity (scaling keeps centre; translate shifts it)
      const applyT = (animate) => {
        zImg.style.transition = animate && !reduceMo ? 'transform .28s cubic-bezier(.32,.72,.25,1)' : 'none';
        zImg.style.transform = 'translate(' + ztx + 'px,' + zty + 'px) scale(' + zs + ')';
        zImg.style.cursor = zs > 1.02 ? 'grab' : 'zoom-in';
      };
      const resetT = () => { zs = 1; ztx = 0; zty = 0; zImg.style.transition = ''; zImg.style.transform = ''; zImg.style.cursor = ''; };
      const measureC0 = () => {
        const r = zImg.getBoundingClientRect();
        let tx = ztx, ty = zty;   // read the LIVE (possibly mid-animation) translate so C0 stays exact if a gesture interrupts an animated zoom
        const cs = getComputedStyle(zImg).transform;
        if (cs && cs !== 'none' && typeof DOMMatrixReadOnly === 'function') { const m = new DOMMatrixReadOnly(cs); tx = m.m41; ty = m.m42; }
        C0x = r.left + r.width / 2 - tx; C0y = r.top + r.height / 2 - ty;
      };
      const clampT = () => {
        let w = zImg.clientWidth, h = zImg.clientHeight;
        const nw = zImg.naturalWidth, nh = zImg.naturalHeight;   // discount object-fit:contain letterboxing
        if (nw && nh) { const bR = w / h, iR = nw / nh; if (iR > bR) h = w / iR; else w = h * iR; }
        const mx = Math.max(0, (w * zs - window.innerWidth) / 2 + 8);
        const my = Math.max(0, (h * zs - window.innerHeight) / 2 + 8);
        ztx = Math.min(mx, Math.max(-mx, ztx));
        zty = Math.min(my, Math.max(-my, zty));
      };
      const zoomToPoint = (fx, fy, target, animate) => {   // scale toward a screen focal point, keeping it fixed
        measureC0();
        const s1 = Math.min(MAX_SCALE, Math.max(1, target));
        ztx = fx - C0x - (s1 / zs) * (fx - C0x - ztx);
        zty = fy - C0y - (s1 / zs) * (fy - C0y - zty);
        zs = s1;
        if (zs <= 1.02) resetT(); else { clampT(); applyT(animate); }
      };

      const pts = new Map();   // active pointers: id -> {x,y}
      let pinch = null, panLast = null, moved = 0, tapT = 0, tapX = 0, tapY = 0;
      const clearGesture = () => { pts.clear(); pinch = null; panLast = null; moved = 0; tapT = 0; }; // wipe pointer/tap state between opens (called only on open/close, never mid-gesture)
      zImg.addEventListener('pointerdown', (e) => {
        if (z.hidden) return;
        zImg.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        moved = 0;
        if (pts.size === 2) {
          measureC0();
          const [a, b] = [...pts.values()];
          pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: zs, tx0: ztx, ty0: zty, fx: (a.x + b.x) / 2, fy: (a.y + b.y) / 2 };
          panLast = null;
        } else { panLast = { x: e.clientX, y: e.clientY }; }
      });
      zImg.addEventListener('pointermove', (e) => {
        const p = pts.get(e.pointerId);
        if (!p) return;
        moved += Math.hypot(e.clientX - p.x, e.clientY - p.y);
        p.x = e.clientX; p.y = e.clientY;
        if (pinch && pts.size >= 2) {
          const [a, b] = [...pts.values()];
          const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
          const fx = (a.x + b.x) / 2, fy = (a.y + b.y) / 2;
          const r = Math.min(MAX_SCALE, Math.max(1, pinch.s0 * d / pinch.d)) / pinch.s0;   // zoom ratio vs gesture start
          zs = pinch.s0 * r;
          ztx = fx - C0x - r * (pinch.fx - C0x - pinch.tx0);   // keep the pinch midpoint anchored (also pans with it)
          zty = fy - C0y - r * (pinch.fy - C0y - pinch.ty0);
          clampT(); applyT(false);
        } else if (panLast && zs > 1.02) {
          ztx += e.clientX - panLast.x; zty += e.clientY - panLast.y;
          panLast = { x: e.clientX, y: e.clientY };
          clampT(); applyT(false);
        }
      });
      const endPtr = (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = null;
        if (pts.size === 1) { const [only] = [...pts.values()]; panLast = { x: only.x, y: only.y }; return; }
        if (pts.size > 0) return;
        panLast = null;
        if (zs <= 1.02) resetT(); else { clampT(); applyT(true); }
        if (moved < 8) {   // a genuine tap (not a drag/pinch): dbl-tap toggles zoom; single tap never dismisses
          const now = (window.performance && performance.now) ? performance.now() : 0;
          if (now && now - tapT < 300 && Math.abs(e.clientX - tapX) < 30 && Math.abs(e.clientY - tapY) < 30) {
            tapT = 0;
            if (zs > 1.02) resetT(); else zoomToPoint(e.clientX, e.clientY, DBL_SCALE, true);
          } else { tapT = now; tapX = e.clientX; tapY = e.clientY; }
        }
      };
      zImg.addEventListener('pointerup', endPtr);
      zImg.addEventListener('pointercancel', endPtr);
      z.addEventListener('wheel', (e) => {
        if (z.hidden) return;
        e.preventDefault();
        zoomToPoint(e.clientX, e.clientY, zs * (e.deltaY < 0 ? 1.15 : 1 / 1.15), false);
      }, { passive: false });
      const onResize = () => { if (z.hidden || zs <= 1.02) return; measureC0(); clampT(); applyT(false); }; // re-fit a zoomed lightbox after rotate/resize (bound only while open)
      let zoomInerted = [];
      const zoomScrollKey = 'pdp-zoom';
      const inertBg = (on) => { // isolate the fullscreen lightbox from AT swipe-nav (focus-trap covers keyboard); only touch elements we set, so it never fights jp-layers
        if (on) { zoomInerted = [...document.querySelectorAll('body > *')].filter((el) => el !== z && !el.inert); zoomInerted.forEach((el) => { el.inert = true; }); }
        else { zoomInerted.forEach((el) => { el.inert = false; }); zoomInerted = []; }
      };

      let zoomOpen = false;
      let zoomHideTimer = 0;
      let zoomOpenFrame = 0;
      const closeZoom = () => {
        if (!zoomOpen) return;
        zoomOpen = false;
        cancelAnimationFrame(zoomOpenFrame);
        clearTimeout(zoomHideTimer);
        zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1); // invalidate any in-flight hi-res preloader for the closing image
        z.classList.remove('is-open');
        resetT();        // drop the pan/zoom transform so the CSS exit animation (current scale → .97) runs
        clearGesture();  // don't carry stale pointer/tap state into the next open
        window.removeEventListener('resize', onResize);
        inertBg(false); // restore the background before focus returns to the opener
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(zoomScrollKey);
        else document.body.classList.remove('no-scroll');
        zoomHideTimer = setTimeout(() => {
          if (zoomOpen) return;
          z.hidden = true; zImg.removeAttribute('src');
        }, 250);
        if (zoomReturn) zoomReturn.focus();
      };
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-jp-stage] .jp-pp__viewer li[data-jp-image].is-active > img');
        if (trigger && !zoomOpen) {
          zoomOpen = true;
          clearTimeout(zoomHideTimer);
          cancelAnimationFrame(zoomOpenFrame);
          zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1);
          zoomReturn = document.activeElement;
          const low = trigger.currentSrc || trigger.src;
          const hi = trigger.closest('li')?.dataset.zoomSrc;
          zImg.src = low; // show the already-cached image instantly...
          if (hi && hi !== low) { // ...then upgrade to the hi-res 2048px master once it loads (no blank flash)
            const gen = (zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1));
            const pre = new Image();
            pre.onload = () => { if (!z.hidden && zImg.dataset.gen === gen) zImg.src = hi; };
            pre.src = hi;
          }
          zImg.alt = trigger.alt || '';
          resetT();        // start every open at fit (scale 1, centred) so the entrance animation is clean
          clearGesture();  // fresh pointer/tap state (defends against a pointer leaked by a prior lossy close)
          window.addEventListener('resize', onResize);
          z.hidden = false;
          zoomOpenFrame = requestAnimationFrame(() => {
            if (!zoomOpen || !z.isConnected) return;
            z.classList.add('is-open');
            // Both the critical shell and deferred CSS hide the control until
            // this frame; close/reset cancels its right to move focus.
            z.querySelector('.jp-zoom__close')?.focus({ preventScroll: true });
          });
          if (window.JapanScissors?.layers?.lockScroll) window.JapanScissors.layers.lockScroll(zoomScrollKey);
          else document.body.classList.add('no-scroll');
          inertBg(true); // AT can't wander behind the fullscreen zoom
          /* S33 §2b · analytics — gallery zoom lightbox opened (blade/finish detail engagement). Non-blocking. */
          try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_zoom_opened' } })); } catch (err) { /* no-op */ }
          return;
        }
        if (!z.hidden && e.target.closest('[data-jp-zoom-close]')) closeZoom(); // NB: image tap no longer closes — it zooms/pans
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeZoom(); });
      z.addEventListener('keydown', (e) => {
        if (z.hidden || e.key !== 'Tab') return;
        /* The lightbox deliberately exposes one keyboard control (Close); keep
           Tab on it instead of allowing focus to fall into browser chrome. */
        e.preventDefault();
        z.querySelector('.jp-zoom__close')?.focus();
      });
      const resetZoom = () => {
        zoomOpen = false;
        clearTimeout(zoomHideTimer);
        cancelAnimationFrame(zoomOpenFrame);
        zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1);
        z.classList.remove('is-open');
        resetT();
        clearGesture();
        window.removeEventListener('resize', onResize);
        inertBg(false);
        z.hidden = true;
        zImg.removeAttribute('src');
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(zoomScrollKey);
        else document.body.classList.remove('no-scroll');
      };
      document.addEventListener('jp:layers:reset', resetZoom);
      window.addEventListener('pagehide', resetZoom);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target?.querySelector?.('[data-jp-stage]')) resetZoom();
      });
      /* keyboard/AT entry point: Enter/Space on the focused active viewer image opens the SAME zoom as a
         pointer click (synthetic .click() reuses the delegated open path; activeElement → the image, so
         focus returns to it on close). Pointer users keep the plain click. */
      document.addEventListener('keydown', (e) => {
        if (!z.hidden) return;
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        const im = e.target.closest && e.target.closest('[data-jp-stage] .jp-pp__viewer li[data-jp-image].is-active > img');
        if (im) { e.preventDefault(); im.click(); }
      });
    }
    /* Make viewer images focusable + labelled as zoom triggers (runs each init — the gallery re-renders on
       section reload). Inactive slides are display:none, so only the ACTIVE image is ever a tab stop.
       aria-label keeps the product alt AND states the zoom affordance; recomputed each pass = idempotent. */
    const jpZoomStage = document.querySelector('[data-jp-stage]');
    if (jpZoomStage) {
      const jpZoomLbl = jpZoomStage.dataset.zoomLabel || 'Zoom image';
      const jpZoomGallery = jpZoomStage.closest('media-gallery');
      const jpZoomSelector = typeof jpZoomGallery?.setActiveMedia === 'function'
        ? '.jp-pp__viewer li[data-jp-image] > img'
        : '.jp-pp__viewer li[data-jp-image].is-active > img';
      jpZoomStage.querySelectorAll(jpZoomSelector).forEach((im) => {
        im.setAttribute('tabindex', '0');
        im.setAttribute('role', 'button');
        im.setAttribute('aria-haspopup', 'dialog');
        im.setAttribute('aria-label', im.alt ? jpZoomLbl + ': ' + im.alt : jpZoomLbl);
      });
    }

    /* ---- ATC label + percut (independent of sticky) ---- */
    atcLabel();
    renderPercut();
    dispatchDate();
    steerCard();

    /* ---- sticky bar: echo the SELECTED variant (size · handedness) so a buyer deep in the
       specs never silently adds the default variant on a £300 tool (P2.2) ---- */
    const updateStickyVar = () => {
      const el = document.querySelector('[data-jp-sticky-var]');
      const vs = document.querySelector('variant-selects');
      if (!el || !vs) return;
      const vals = [];
      /* scope to OUR option fieldsets so a third-party widget's injected fieldset can't pollute the echo */
      vs.querySelectorAll('fieldset.jp-pp__opt').forEach((fs) => {
        const c = fs.querySelector('input:checked') || fs.querySelector('select');
        if (c && c.value) vals.push(c.value.trim());
      });
      el.textContent = vals.join(' · ');
    };
    updateStickyVar();

    /* ---- P3.x · size-safety + availability layer ----------------------------------------------
       Two calm, honest jobs off the SELECTED variant, no urgency/countdown/%:
         · size-safety: on a multi-size tool, block purchase until a size is explicitly picked;
           the sticky bar scrolls + focuses the fieldset with a "Choose your size" nudge.
         · availability: recompute the availability line per selected variant, accent the ATC by state
           (in-stock vs forged-to-order via tokens), prompt "choose a size to see availability" when
           unselected, and gently note "other sizes are in stock" when the pick is forged-to-order.
       Reads state from the already-rendered per-size dots (.jp-pp__dot--in/pre) so there is one
       source of truth (the server-rendered chips) and no re-derivation. */
    let jpSizeSafety = null;
    const buyPanel = document.querySelector('[data-jp-buy-panel]');
    /* Dawn replaces the whole <variant-selects> subtree on every variant change (global.js viewTransition),
       so re-query the nudge marker + its fieldset LIVE rather than caching a node that goes detached. */
    const sizeNudge = () => document.querySelector('[data-jp-size-nudge]');
    const sizeFieldset = () => { const n = sizeNudge(); return n ? n.closest('fieldset') : null; };
    if (buyPanel && !buyPanel.dataset.jpAvailBound) {
      buyPanel.dataset.jpAvailBound = '1';
      const strChoose = buyPanel.dataset.jpChooseSize || 'Choose your size';
      const strChooseAvail = buyPanel.dataset.jpChooseSizeAvail || 'Choose a size to see availability';
      const strOthers = buyPanel.dataset.jpAvailOthers || 'Other sizes are in stock';
      const strVariantLoading = buyPanel.dataset.jpVariantLoading || 'Loading...';
      const strVariantError = buyPanel.dataset.jpVariantError || 'We could not update this option. Check your connection and try again.';
      const availPrompt = document.querySelector('[data-jp-avail-prompt]');
      const mainAtcBtn = () => document.querySelector('[data-jp-atc]');

      /* size radios (genuine size option only — the nudge marker sits in that fieldset) */
      const sizeRadios = () => { const fs = sizeFieldset(); return fs ? [...fs.querySelectorAll('input[type="radio"]')] : []; };
      const hasMultiSize = () => sizeRadios().length > 1;
      /* "touched" = the shopper has fired a change on a size radio at least once this page-view.
         A page can load with a size pre-checked (Dawn), but that is NOT an explicit human choice,
         so we track intent separately from :checked. */
      let sizeTouched = buyPanel.dataset.jpExplicitVariant === 'true';
      let variantPending = false;
      let variantInvalid = false;
      let variantError = false;
      const readRenderedVariant = () => {
        const json = buyPanel.querySelector('variant-selects [data-selected-variant]')?.textContent.trim();
        if (!json) return null;
        try { return JSON.parse(json); } catch (_) { return null; }
      };
      let lastResolvedVariant = readRenderedVariant();
      let lastResolvedSizeTouched = sizeTouched;
      const checkedSize = () => sizeRadios().find((r) => r.checked) || null;
      /* stock state of a size radio, read from its rendered dot: 'in' | 'pre' | 'out' | null */
      const radioState = (r) => {
        if (!r) return null;
        const dot = r.parentElement && r.parentElement.querySelector('.jp-pp__dot');
        if (!dot) return null;
        if (dot.classList.contains('jp-pp__dot--in')) return 'in';
        if (dot.classList.contains('jp-pp__dot--pre')) return 'pre';
        if (dot.classList.contains('jp-pp__dot--out')) return 'out';
        return null;
      };
      const syncStickyAtcState = () => {
        const mainButton = mainAtcBtn();
        const busy = variantPending || Boolean(buyPanel.querySelector('product-form')?.addRequest);
        if (busy) mainButton?.setAttribute('aria-busy', 'true');
        else mainButton?.removeAttribute('aria-busy');
        const stickyButton = document.querySelector('[data-jp-sticky-atc]');
        if (!stickyButton) return;
        const stickyPrice = document.querySelector('[data-jp-sticky-price]');
        const awaiting = hasMultiSize() && !sizeTouched;
        const mainLabel = mainButton?.querySelector('span')?.textContent.trim().split(' — ')[0] || '';
        let label = mainLabel;
        if (variantPending) label = strVariantLoading;
        else if (variantInvalid) label = window.variantStrings?.unavailable || mainLabel;
        else if (awaiting) label = strChoose;
        const disabled = busy || variantInvalid || (!awaiting && Boolean(mainButton?.disabled
          || mainButton?.getAttribute('aria-disabled') === 'true'));
        stickyButton.disabled = disabled;
        if (disabled) stickyButton.setAttribute('aria-disabled', 'true');
        else stickyButton.removeAttribute('aria-disabled');
        if (busy) stickyButton.setAttribute('aria-busy', 'true');
        else stickyButton.removeAttribute('aria-busy');
        if (stickyPrice) stickyPrice.hidden = variantPending || variantInvalid;
        if (label) stickyButton.textContent = label;
      };
      const syncChoiceGate = (selectedRadio, resolvedVariant) => {
        const awaiting = hasMultiSize() && !sizeTouched;
        const purchaseGated = awaiting || variantPending || variantInvalid;
        // Stock and dispatch describe a resolved choice, never the default or an in-flight option.
        const inventory = buyPanel.querySelector('[id^="Inventory-"]');
        if (inventory) inventory.hidden = purchaseGated;
        const fieldset = sizeFieldset();
        fieldset?.classList.toggle('is-awaiting-choice', awaiting);
        const selectionLabel = fieldset?.querySelector('.jp-pp__opt-head .lbl span');
        const button = mainAtcBtn();
        const dynamicPayment = buyPanel.querySelector('.jp-pp__dynamic');
        if (variantPending || buyPanel.querySelector('product-form')?.addRequest) button?.setAttribute('aria-busy', 'true');
        else button?.removeAttribute('aria-busy');
        if (purchaseGated) {
          if (awaiting && !variantPending) {
            sizeRadios().forEach((radio) => { radio.checked = false; });
            if (selectionLabel && selectionLabel.textContent !== `· ${strChoose}`) selectionLabel.textContent = `· ${strChoose}`;
          }
          if (button) {
            /* Only the untouched-size state may survive a server refresh. A
               transient pending/invalid state is replaced by ProductInfo's
               authoritative response; preserving it would leave a newly
               selected in-stock variant permanently disabled. */
            if (awaiting) button.dataset.jpSizeGated = 'true';
            else delete button.dataset.jpSizeGated;
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            const label = button.querySelector('span');
            if (label && awaiting && label.textContent !== strChoose) label.textContent = strChoose;
            else if (label && variantPending && label.textContent !== strVariantLoading) label.textContent = strVariantLoading;
            else if (label && variantInvalid) label.textContent = window.variantStrings?.unavailable || label.textContent;
          }
          if (dynamicPayment) {
            dynamicPayment.hidden = true;
            if (awaiting) dynamicPayment.dataset.jpSizeGated = 'true';
            else delete dynamicPayment.dataset.jpSizeGated;
          }
        } else {
          const chosen = selectedRadio || checkedSize();
          if (selectionLabel && chosen) selectionLabel.textContent = `· ${chosen.value}`;
          if (button?.dataset.jpSizeGated) {
            delete button.dataset.jpSizeGated;
            if (resolvedVariant?.available && !button.disabled) button.removeAttribute('aria-disabled');
            else if (button.disabled) button.setAttribute('aria-disabled', 'true');
            const sync = atcLabel();
            if (sync) sync();
          }
          if (dynamicPayment) {
            /* The server-rendered ATC button includes inventory-policy and
               quantity-rule guards that Shopify's public variant JSON omits.
               Treat its disabled state as authoritative for accelerated
               checkout, with variant.available as an additional fail-closed
               signal when a resolved variant is present. A native delayed
               variant stays on the normal form path so accelerated checkout
               cannot bypass its disclosure or the mixed-cart review. */
            dynamicPayment.hidden = Boolean(button?.disabled)
              || selectedState() === 'pre'
              || Boolean(resolvedVariant && !resolvedVariant.available);
            delete dynamicPayment.dataset.jpSizeGated;
          }
        }
        syncStickyAtcState();
        updateStickyVar();
      };
      const anyOtherInStock = (exclude) =>
        sizeRadios().some((r) => r !== exclude && radioState(r) === 'in');

      /* current selected-variant availability state, preferring the authoritative stock block */
      const selectedState = () => {
        const stockBlock = document.querySelector('[id^="Inventory-"]');
        const line = stockBlock && stockBlock.querySelector('.jp-pp__stock-line');
        if (line) {
          if (line.classList.contains('jp-pp__stock--in')) return 'in';
          if (line.classList.contains('jp-pp__stock--pre')) return 'pre';
          return 'out';
        }
        return radioState(checkedSize());
      };

      /* The order-time promise controls are form-associated inputs inside
         Dawn's Inventory-* server swap target. Keep them successful only while
         a resolved variant is the exact native delayed/orderable state. Clear
         them immediately during an unresolved/impossible selection so a stale
         promise can never hitch a ride on an ordinary line. A successful Dawn
         response (or its resolved-state rollback) supplies fresh values. */
      const promiseFields = () => [...buyPanel.querySelectorAll('[data-jp-promise-field]')];
      const syncFulfilmentPromise = () => {
        const variantId = buyPanel.querySelector('input.product-variant-id[name="id"]')?.value;
        const delayed = Boolean(variantId) && !variantPending && !variantInvalid && selectedState() === 'pre';
        promiseFields().forEach((field) => {
          const hasPublicDispatch = field.dataset.jpPromiseField !== 'dispatch' || Boolean(field.value.trim());
          field.disabled = !delayed || !hasPublicDispatch;
          if (!delayed) field.value = '';
        });
      };

      /* accent the ATC (main + sticky) by state — calm token tint only, never a hard alarm */
      const accentAtc = (state) => {
        [mainAtcBtn(), document.querySelector('[data-jp-sticky-atc]')].forEach((btn) => {
          if (!btn) return;
          btn.classList.toggle('jp-atc--instock', state === 'in');
          btn.classList.toggle('jp-atc--forged', state === 'pre');
        });
      };

      /* recompute the JS availability prompt line + ATC accent for the current selection */
      const refreshAvailability = () => {
        syncFulfilmentPromise();
        syncChoiceGate();
        /* while we're still asking a multi-size shopper to choose, DON'T accent the ATC to the default
           variant's state — an accent there would contradict the "choose a size" prompt. Accent only
           once the choice is real (touched), or when there's no size decision to make. */
        const asking = hasMultiSize() && !sizeTouched;
        accentAtc(asking || variantPending || variantInvalid ? null : selectedState());
        buyPanel.closest('product-info')?.dispatchEvent(new CustomEvent('product-info:purchase-state', { bubbles: true }));
        if (!availPrompt) return;
        if (variantError) {
          availPrompt.textContent = strVariantError;
          availPrompt.className = 'jp-pp__avail-prompt jp-pp__avail-prompt--ask';
          availPrompt.hidden = false;
          return;
        }
        /* Stock/choice prompts are size-specific; transport errors are not. */
        if (!hasMultiSize()) { availPrompt.hidden = true; availPrompt.textContent = ''; availPrompt.className = 'jp-pp__avail-prompt'; return; }
        if (asking) {
          /* no explicit choice yet — invite one rather than implying the default variant's state */
          availPrompt.textContent = strChooseAvail;
          availPrompt.className = 'jp-pp__avail-prompt jp-pp__avail-prompt--ask';
          availPrompt.hidden = false;
          return;
        }
        if (variantPending || variantInvalid) {
          availPrompt.hidden = true;
          availPrompt.textContent = '';
          availPrompt.className = 'jp-pp__avail-prompt';
          return;
        }
        /* a size is chosen: if it's forged-to-order but stock exists elsewhere, gently surface that */
        if (selectedState() === 'pre' && anyOtherInStock(checkedSize())) {
          availPrompt.textContent = strOthers;
          availPrompt.className = 'jp-pp__avail-prompt jp-pp__avail-prompt--others';
          availPrompt.hidden = false;
          return;
        }
        availPrompt.hidden = true;
        availPrompt.textContent = '';
        availPrompt.className = 'jp-pp__avail-prompt';
      };

      /* Mark intent on a real value change. The initial default is un-checked,
         while a deep-linked variant is already explicit; same-value clicks must
         never create a pending state that Dawn cannot resolve. */
      const markSizeTouched = (e) => {
        const r = e.target.closest && e.target.closest('input[type="radio"]');
        if (!r || !sizeRadios().includes(r)) return;
        sizeTouched = true;
        syncChoiceGate(r);
        const n = sizeNudge();
        if (n) { n.hidden = true; n.textContent = ''; }
        refreshAvailability();
      };
      /* Capture intent before <variant-selects>' bubble listener publishes and
         queues its render work. On touch browsers, marking this in bubble phase
         allowed the untouched gate to rewrite the active radio mid-activation,
         trapping the page in native event/microtask churn. */
      buyPanel.addEventListener('change', markSizeTouched, true);

      jpSizeSafety = {
        /* true only when a genuine multi-size product has had no explicit size choice yet */
        needsChoice: () => hasMultiSize() && !sizeTouched,
        isResolving: () => variantPending,
        isInvalid: () => variantInvalid,
        syncSticky: syncStickyAtcState,
        promptChoice: () => {
          const n = sizeNudge();
          if (n) { n.textContent = strChoose; n.hidden = false; }
          if (availPrompt) refreshAvailability();
          const fs = sizeFieldset();
          if (fs) {
            const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            fs.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
            /* focus the checked radio (or the first) so keyboard/AT lands on the choice; delayed a touch so
               the smooth-scroll isn't yanked back by the focus jump */
            const target = checkedSize() || sizeRadios()[0];
            if (target) window.setTimeout(() => { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }, reduce ? 0 : 260);
          }
        },
        refresh: refreshAvailability,
      };
      buyPanel.addEventListener('submit', (event) => {
        if (!jpSizeSafety.needsChoice() && !variantPending && !variantInvalid) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (jpSizeSafety.needsChoice()) jpSizeSafety.promptChoice();
      }, true);
      const purchaseOwner = buyPanel.closest('product-info');
      // Shared optional purchase surfaces consume the owning native form and
      // this explicit-choice state. They never infer eligibility from a card.
      if (purchaseOwner) purchaseOwner.jpPurchaseState = () => {
        const button = mainAtcBtn();
        const form = buyPanel.querySelector('product-form');
        const variantId = buyPanel.querySelector('input.product-variant-id[name="id"]')?.value || '';
        return { variantId, ready: Boolean(variantId && button && !button.disabled
          && button.getAttribute('aria-disabled') !== 'true' && !form?.addRequest
          && !variantPending && !variantInvalid && !(hasMultiSize() && !sizeTouched)) };
      };
      const purchaseButton = mainAtcBtn();
      if (purchaseOwner && purchaseButton) {
        // Native cart refreshes and Add finalizers can change eligibility without
        // replacing Inventory. Notify optional consumers and mirror busy state;
        // never rerun the choice gate from its own button mutations.
        trackProductObserver(new MutationObserver(() => {
          syncStickyAtcState();
          purchaseOwner.dispatchEvent(new CustomEvent('product-info:purchase-state', { bubbles: true }));
        })).observe(purchaseButton, {
          attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true, characterData: true, subtree: true,
        });
      }
      const productSectionId = purchaseOwner?.dataset.section;

      /* ---- variant region refresh (1.2, atomic 2026-07-28): the above-fold spec strip
         and the product JSON-LD are swapped from the SAME parsed document Dawn used for
         its commerce swap (variantChange.data.html), so facts can never come from a
         different response than price/SKU/inventory. Only the separate tabs section is
         fetched here, through ONE abortable, latest-wins transport; older requests are
         aborted and stale responses ignored, so rapid hand/size changes cannot leave
         mismatched tabs. If the tabs fetch fails or comes back unusable, the page
         navigates to the exact ?variant= URL (the same no-JS path) rather than leaving
         new-variant commerce next to old-variant facts. */
      const tabsSectionEl = document.querySelector('[data-jp-ptabs]')?.closest('.shopify-section');
      const tabsSectionId = tabsSectionEl?.id?.startsWith('shopify-section-') ? tabsSectionEl.id.slice('shopify-section-'.length) : null;
      // The editor can replace only the outer tabs section while this product
      // controller survives. Resolve that same section identity at every effect.
      const currentTabsSection = () => tabsSectionId ? document.getElementById(`shopify-section-${tabsSectionId}`) : null;
      const productUrl = buyPanel.closest('product-info')?.dataset.url;
      let variantRegionController = null;
      let variantRegionSeq = 0;
      let variantAnnouncementTimer = 0;
      // Option intent retires dependent work immediately, before Shopify resolves it.
      const invalidateVariantRegions = () => {
        variantRegionSeq += 1;
        variantRegionController?.abort();
        clearTimeout(variantAnnouncementTimer);
        if (variantStatus) variantStatus.textContent = '';
        // Component facts belong to the resolved selection. Retire old rows at
        // intent; only the exact variant's new server markup reveals them again.
        currentTabsSection()?.querySelectorAll('[data-jp-component-variant]').forEach((row) => { row.hidden = true; });
      };
      let variantStatus = document.getElementById('jp-variant-status');
      if (!variantStatus) {
        variantStatus = document.createElement('div');
        variantStatus.id = 'jp-variant-status';
        variantStatus.className = 'visually-hidden';
        variantStatus.setAttribute('aria-live', 'polite');
        buyPanel.appendChild(variantStatus);
      }
      const swapMainFactRegions = (mainDoc) => {
        if (!mainDoc) return;
        const liveSlot = document.querySelector('[data-jp-spec-strip-slot]');
        const nextSlot = mainDoc.querySelector('[data-jp-spec-strip-slot]');
        if (liveSlot && nextSlot) {
          const liveDisclosure = liveSlot.querySelector('details[data-jp-spec-strip]');
          const nextDisclosure = nextSlot.querySelector('details[data-jp-spec-strip]');
          if (liveDisclosure && nextDisclosure) nextDisclosure.open = liveDisclosure.open;
          const focused = liveSlot.contains(document.activeElement);
          const focusedLink = focused && document.activeElement?.matches('[data-jp-open-tab]');
          liveSlot.replaceChildren(...[...nextSlot.childNodes]);
          // A keyboard user can open supporting detail while a native variant response settles.
          if (focused) {
            const target = liveSlot.querySelector(focusedLink ? '[data-jp-open-tab]' : 'summary')
              || liveSlot.querySelector('summary')
              || sizeFieldset()?.querySelector('input[type="radio"]:checked')
              || sizeFieldset()?.querySelector('input[type="radio"]')
              || buyPanel.closest('product-info')?.querySelector('.jp-pp__title');
            if (target && !target.matches('a, button, input, summary, [tabindex]')) target.setAttribute('tabindex', '-1');
            target?.focus({ preventScroll: true });
          }
        }
        const liveSchema = document.querySelector('script[data-jp-schema-product]');
        const nextSchema = mainDoc.querySelector('script[data-jp-schema-product]');
        if (liveSchema && nextSchema) liveSchema.textContent = nextSchema.textContent;
      };
      const refreshVariantRegions = (variantId, variant, mainDoc) => {
        if (!variantId || !productUrl || !productSectionId) return;
        invalidateVariantRegions();
        const controller = trackProductController(new AbortController());
        variantRegionController = controller;
        const seq = ++variantRegionSeq;
        const isCurrent = () => seq === variantRegionSeq && !controller.signal.aborted
          && buyPanel.isConnected && !variantPending && !variantInvalid
          && String(variantIdInput?.value) === String(variantId);
        /* Atomic part 1: spec strip + JSON-LD from Dawn's own parsed document — no request. */
        swapMainFactRegions(mainDoc);
        const currentSection = currentTabsSection();
        if (!currentSection) {
          initProductTabs();
          return;
        }
        /* Atomic part 2: only the tabs section travels. aria-busy marks the region as
           settling; failure fails closed to a full server render of the exact variant. */
        currentSection.hidden = false;
        currentSection.setAttribute('aria-busy', 'true');
        const failClosedReload = () => {
          const currentSection = currentTabsSection();
          if (!isCurrent() || !currentSection) return;
          currentSection.removeAttribute('aria-busy');
          window.location.assign(`${productUrl}?variant=${encodeURIComponent(variantId)}`);
        };
        fetch(`${productUrl}?section_id=${encodeURIComponent(tabsSectionId)}&variant=${encodeURIComponent(variantId)}`, { signal: controller.signal, credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : null))
          .then((tabsHtml) => {
            if (!isCurrent()) return; /* latest option intent owns all dependent effects */
            const tabsSectionEl = currentTabsSection();
            if (!tabsSectionEl) return;
            tabsSectionEl.removeAttribute('aria-busy');
            if (!tabsHtml) { failClosedReload(); return; }
            const tabsDoc = new DOMParser().parseFromString(tabsHtml, 'text/html');
            const nextSection = tabsDoc.getElementById(`shopify-section-${tabsSectionId}`);
            if (!nextSection) { failClosedReload(); return; }
            const componentRows = [...nextSection.querySelectorAll('[data-jp-component-variant]')];
            if (componentRows.some((row) => row.dataset.jpComponentVariant !== String(variantId))) {
              failClosedReload(); return;
            }
            // Read the shopper's latest tab/focus immediately before replacement.
            const activeTabKey = tabsSectionEl.querySelector('[role="tab"][aria-selected="true"]')?.dataset.jpTab;
            const restoreFocus = tabsSectionEl.contains(document.activeElement);
            tabsSectionEl.replaceChildren(...[...nextSection.childNodes]);
            initProductTabs(); /* rebind fresh strip link + tabs nodes (jpBound-guarded) */
            const availableTabs = [...tabsSectionEl.querySelectorAll('[role="tab"]')];
            const tab = availableTabs.find((candidate) => candidate.dataset.jpTab === activeTabKey) || availableTabs[0];
            tab?.click();
            if (restoreFocus) tab?.focus({ preventScroll: true });
            if (variantStatus) {
              const label = variant?.title || '';
              variantStatus.textContent = '';
              if (label) {
                const template = buyPanel.dataset.jpVariantShowing || 'Showing [variant]. Specifications and details updated.';
                variantAnnouncementTimer = window.setTimeout(() => {
                  if (isCurrent()) variantStatus.textContent = template.replace('[variant]', label);
                }, 30);
              }
            }
          }).catch((error) => {
            if (error?.name === 'AbortError') return;
            if (!isCurrent()) return;
            failClosedReload(); /* never leave mixed-variant fact state */
          });
      };

      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      const variantUnsubscribers = [];
      if (typeof subscribe === 'function' && events?.optionValueSelectionChange) {
        variantUnsubscribers.push(subscribe(events.optionValueSelectionChange, ({ data }) => {
          const target = data?.event?.target;
          if (!target || !buyPanel.contains(target)) return;
          invalidateVariantRegions();
          currentTabsSection()?.setAttribute('aria-busy', 'true');
          variantError = false;
          variantInvalid = false;
          variantPending = true;
          queueMicrotask(() => {
            syncChoiceGate(target);
            refreshAvailability();
          });
        }));
      }
      if (typeof subscribe === 'function' && events?.variantChange) {
        variantUnsubscribers.push(subscribe(events.variantChange, ({ data }) => {
          if (productSectionId && String(data?.sectionId) !== String(productSectionId)) return;
          variantError = false;
          variantPending = false;
          variantInvalid = false;
          lastResolvedVariant = data?.variant || readRenderedVariant();
          lastResolvedSizeTouched = sizeTouched;
          syncChoiceGate(null, data?.variant);
          refreshAvailability();
          refreshVariantRegions(data?.variant?.id, data?.variant, data?.html);
        }));
      }
      /* Dawn does not publish variantChange for an impossible combination. It
         does clear the authoritative form variant id before setUnavailable(),
         which gives this layer an exact terminal signal instead of leaving both
         purchase controls indefinitely labelled Loading. */
      const variantIdInput = buyPanel.querySelector('input.product-variant-id[name="id"]');
      variantIdInput?.addEventListener('change', () => {
        if (variantIdInput.value || !variantPending) return;
        variantPending = false;
        variantInvalid = !(hasMultiSize() && !sizeTouched);
        invalidateVariantRegions();
        const tabsSectionEl = currentTabsSection();
        if (tabsSectionEl) {
          tabsSectionEl.removeAttribute('aria-busy');
          tabsSectionEl.hidden = true;
        }
        queueMicrotask(() => {
          syncChoiceGate();
          refreshAvailability();
        });
      });
      const productInfo = buyPanel.closest('product-info');
      const onVariantError = () => {
        variantPending = false;
        variantError = true;
        sizeTouched = lastResolvedSizeTouched;

        const resolved = lastResolvedVariant;
        const values = Array.isArray(resolved?.options)
          ? resolved.options
          : [resolved?.option1, resolved?.option2, resolved?.option3].filter((value) => value != null);
        const selectors = buyPanel.querySelector('variant-selects');
        if (resolved && selectors && values.length) {
          [...selectors.querySelectorAll('fieldset.jp-pp__opt')].forEach((fieldset, index) => {
            fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.checked = radio.value === values[index];
            });
          });
          if (variantIdInput) variantIdInput.value = String(resolved.id);
          /* ProductInfo has already restored the last server-rendered button,
             label and quantity limits. Do not derive an enabled state from the
             stock chip here: the button may also be disabled because the cart
             has reached the variant's effective maximum. */
          variantInvalid = false;
        } else {
          variantInvalid = true;
        }
        syncChoiceGate(null, resolved);
        refreshAvailability();
        // A's tabs may have been aborted when B was attempted. Rollback must
        // deliberately fetch A's facts again, not leave the cancelled request in charge.
        if (resolved && !variantInvalid) refreshVariantRegions(resolved.id, resolved, null);
      };
      productInfo?.addEventListener('product-info:variant-error', onVariantError);
      if (variantUnsubscribers.length || productInfo) {
        let variantSubscriptionsCleaned = false;
        let cleanupOnSectionUnload = null;
        const cleanupVariantSubscriptions = () => {
          if (variantSubscriptionsCleaned) return;
          variantSubscriptionsCleaned = true;
          invalidateVariantRegions();
          variantUnsubscribers.forEach((unsubscribe) => unsubscribe());
          productInfo?.removeEventListener('product-info:variant-error', onVariantError);
          if (cleanupOnSectionUnload) document.removeEventListener('shopify:section:unload', cleanupOnSectionUnload);
          if (activeProductCleanup === cleanupVariantSubscriptions) activeProductCleanup = null;
        };
        buyPanel.__jpProductCleanup = cleanupVariantSubscriptions;
        activeProductCleanup = cleanupVariantSubscriptions;
        cleanupOnSectionUnload = (event) => {
          if (event.target?.contains?.(buyPanel)) cleanupVariantSubscriptions();
        };
        document.addEventListener('shopify:section:unload', cleanupOnSectionUnload);
      }

      /* Hand/other-option changes can replace the whole variant-selects node
         before a complete variant exists. Reapply the untouched-size gate to
         that fresh markup without creating a child-mutation loop. */
      trackProductObserver(new MutationObserver(() => {
        if (!sizeTouched && sizeFieldset()) queueMicrotask(() => syncChoiceGate());
      })).observe(buyPanel, { childList: true });

      /* the Inventory- block is Dawn's authoritative swap target for the selected variant's stock —
         observe it so the ATC accent + prompt recompute the moment the real state lands (independent
         of the size-radio change event, which fires before Dawn's fetch resolves). Idempotent. */
      const invBlock = document.querySelector('[id^="Inventory-"]');
      if (invBlock && !invBlock.dataset.jpAvailBound) {
        invBlock.dataset.jpAvailBound = '1';
        trackProductObserver(new MutationObserver(refreshAvailability)).observe(invBlock, { childList: true, subtree: true });
      }
      refreshAvailability();
    }

    /* ---- shared price-block observer: sticky price + percut + ATC price ---- */
    const priceBlock = document.querySelector('[id^="price-"]');
    if (priceBlock && !priceBlock.dataset.jpPriceBound) {
      priceBlock.dataset.jpPriceBound = '1';
      trackProductObserver(new MutationObserver(() => {
        renderPercut();
        updateStickyVar();
        const sync = atcLabel();
        if (sync) sync();
        const now = priceBlock.querySelector('.jp-pp__now');
        const stickyPrice = document.querySelector('[data-jp-sticky-price]');
        if (now && stickyPrice) {
          const note = stickyPrice.querySelector('small');
          stickyPrice.textContent = now.textContent.trim() + (note ? ' ' : '');
          if (note) stickyPrice.appendChild(note);
        }
        /* Keep the sticky CTA on the same state machine as the real form. The
           fallback covers a non-JP product form where no size-safety layer exists. */
        if (jpSizeSafety?.syncSticky) jpSizeSafety.syncSticky();
        else {
          const mainAtc = document.querySelector('[id^="ProductSubmitButton-"]');
          const stickyAtc = document.querySelector('[data-jp-sticky-atc]');
          if (mainAtc && stickyAtc) {
            stickyAtc.disabled = mainAtc.disabled;
            const ml = mainAtc.querySelector('span');
            if (ml) stickyAtc.textContent = ml.textContent.trim().split(' — ')[0];
          }
        }
      /* Dawn replaces priceBlock.innerHTML, so direct-child observation is
         sufficient. Do not watch descendants: renderPercut() updates one of
         them and must never be able to schedule its own observer callback. */
      })).observe(priceBlock, { childList: true });
    }

    /* ---- sticky buy bar ---- */
    const sticky = document.querySelector('[data-jp-sticky]');
    const panel = document.querySelector('[data-jp-buy-sentinel]') || document.querySelector('[data-jp-buy-panel]');
    if (sticky && panel && !sticky.dataset.jpBound && 'IntersectionObserver' in window) {
      sticky.dataset.jpBound = '1';
      /* Reserve the real fixed controls for anchor/focus scrolling. The mobile
         nav is optional and its measured box already includes the safe area.
         Keep this independent of document padding and the visibility sentinel. */
      const layoutController = trackProductController(new AbortController());
      const rootStyle = document.documentElement.style;
      const layoutProperties = ['--jp-sticky-nav-height', '--jp-sticky-occupied-height'];
      const previousLayout = layoutProperties.map((name) => [name, rootStyle.getPropertyValue(name), rootStyle.getPropertyPriority(name)]);
      let measuredNav = null;
      let measuredFooter = null;
      let footerInView = false; // also true until a replacement footer is measured
      let layoutFrame = 0;
      let layoutDisposed = false;
      const updateStickyVisibility = () => {
        /* An instant jump can skip the sentinel without changing its IO state
           (outside below -> outside above). Current geometry, not a queued
           observer snapshot, decides whether the purchase panel has passed. */
        const rect = panel.isConnected ? panel.getBoundingClientRect() : null;
        const show = !!rect && rect.height > 0 && rect.bottom <= 0 && !footerInView;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      const renderedHeight = (element) => {
        if (!element?.isConnected || element.hidden) return 0;
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return 0;
        const height = element.getBoundingClientRect().height;
        return Number.isFinite(height) ? Math.max(0, Math.ceil(height)) : 0;
      };
      const updateStickyLayout = () => {
        if (layoutDisposed) return;
        const nav = document.querySelector('.jp-mnav');
        if (nav !== measuredNav) {
          if (measuredNav) layoutObserver?.unobserve(measuredNav);
          measuredNav = nav;
          if (measuredNav) layoutObserver?.observe(measuredNav);
        }
        const footer = document.querySelector('.jp-foot, .footer, footer');
        if (footer !== measuredFooter) {
          if (measuredFooter) footerObserver.unobserve(measuredFooter);
          measuredFooter = footer;
          // Avoid retaining the removed footer's state or showing over a new
          // footer while its first asynchronous intersection is still pending.
          footerInView = !!measuredFooter;
          if (measuredFooter) footerObserver.observe(measuredFooter);
        }
        // Reconcile visibility before measuring the occupied fixed-bar space.
        updateStickyVisibility();
        const navHeight = renderedHeight(measuredNav);
        const stickyHeight = sticky.classList.contains('is-visible') ? renderedHeight(sticky) : 0;
        [navHeight, navHeight + stickyHeight].forEach((height, index) => {
          const value = `${height}px`;
          if (rootStyle.getPropertyValue(layoutProperties[index]) !== value) rootStyle.setProperty(layoutProperties[index], value);
        });
      };
      const scheduleStickyLayout = () => {
        if (layoutDisposed || layoutFrame) return;
        layoutFrame = window.requestAnimationFrame(() => { layoutFrame = 0; updateStickyLayout(); });
      };
      const layoutObserver = 'ResizeObserver' in window
        ? trackProductObserver(new ResizeObserver(scheduleStickyLayout)) : null;
      layoutObserver?.observe(sticky);
      layoutObserver?.observe(panel);
      window.addEventListener('scroll', scheduleStickyLayout, { passive: true, signal: layoutController.signal });
      window.addEventListener('resize', scheduleStickyLayout, { passive: true, signal: layoutController.signal });
      window.addEventListener('pageshow', scheduleStickyLayout, { signal: layoutController.signal });
      // Footer editor updates can replace either fixed nav or footer while the
      // same PDP remains mounted. Reacquire both on the next animation frame.
      document.addEventListener('shopify:section:load', scheduleStickyLayout, { signal: layoutController.signal });
      document.addEventListener('shopify:section:unload', scheduleStickyLayout, { signal: layoutController.signal });
      layoutController.__jpDispose = () => {
        layoutDisposed = true;
        window.cancelAnimationFrame(layoutFrame);
        previousLayout.forEach(([name, value, priority]) => {
          if (value) rootStyle.setProperty(name, value, priority);
          else rootStyle.removeProperty(name);
        });
        sticky.classList.remove('is-visible');
        sticky.setAttribute('aria-hidden', 'true');
        delete sticky.dataset.jpBound;
      };
      trackProductObserver(new IntersectionObserver(
        scheduleStickyLayout,
        { threshold: 0 }
      )).observe(panel);
      const footerObserver = trackProductObserver(new IntersectionObserver(
        (entries) => {
          if (layoutDisposed) return;
          // unobserve does not cancel queued entries from the detached footer.
          let entry;
          for (const item of entries) {
            if (item.target === measuredFooter && item.target.isConnected) entry = item;
          }
          if (!entry) return;
          footerInView = entry.isIntersecting;
          scheduleStickyLayout();
        },
        { threshold: 0 }
      ));
      updateStickyLayout();
      sticky.querySelector('[data-jp-sticky-atc]')?.addEventListener('click', () => {
        /* P3.x · size-safety: on a multi-size tool the sticky bar hides the size chips, so if the
           shopper has NOT explicitly chosen a size, don't silently add the default variant — send them
           to the size fieldset with a calm "Choose your size" nudge instead. Chosen → submit as normal. */
        if (jpSizeSafety && jpSizeSafety.needsChoice()) { jpSizeSafety.promptChoice(); return; }
        if (jpSizeSafety?.isResolving() || jpSizeSafety?.isInvalid()) return;
        document.querySelector('[id^="ProductSubmitButton-"]')?.click();
      }, { signal: layoutController.signal });
    }

    /* ---- P2.1 · notify-me: AJAX-submit the back-in-stock contact form so the shopper stays on the
       unavailable variant + gets an inline success; degrades to a full-page post without JS ---- */
    if (!window.__jpNotifyBound) {
      window.__jpNotifyBound = true;
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-jp-notify] form');
        if (!form) return;
        if (!form.checkValidity()) return; // let native validation show; never AJAX (or disable the button on) an invalid form
        e.preventDefault();
        const box = form.closest('[data-jp-notify]');
        const ok = box && box.querySelector('[data-jp-notify-ok]');
        const err = box && box.querySelector('[data-jp-notify-err]');
        const btn = form.querySelector('button[type="submit"]');
        if (err) err.hidden = true;
        if (btn) btn.disabled = true;
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } })
          .then((r) => { if (!r.ok) throw new Error('http'); return r.text(); })
          .then((responseText) => {
            const responseDoc = new DOMParser().parseFromString(responseText, 'text/html');
            const responseForm = responseDoc.querySelector('#JpNotify');
            const confirmed = responseForm?.querySelector('.jp-pp__notify-ok[role="status"]');
            if (!confirmed) throw new Error('unconfirmed');
            form.hidden = true;
            if (ok) ok.hidden = false;
            try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_notify_submit' } })); } catch (e) { /* no-op */ }
          })
          .catch(() => { if (btn) btn.disabled = false; if (err) err.hidden = false; });
      });
    }

    /* ---- share ---- */
    document.querySelectorAll('[data-jp-share]').forEach((btn) => {
      if (btn.dataset.jpBound) return;
      btn.dataset.jpBound = '1';
      btn.addEventListener('click', async () => {
        const url = btn.dataset.shareUrl || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url });
          } else {
            await navigator.clipboard.writeText(url);
            const original = btn.innerHTML;
            btn.textContent = btn.dataset.copiedText || 'Link copied';
            setTimeout(() => { btn.innerHTML = original; }, 1600);
          }
        } catch {}
      });
    });

    /* ---- product tabs (ARIA tabs w/ arrow keys + #jp-reviews deep link) ----
       Named + re-runnable: the variant region refresh (1.2) swaps the tabs
       section's markup and must re-bind interactions on the fresh nodes. */
    const initProductTabs = () => {
    document.querySelectorAll('[data-jp-ptabs]').forEach((root) => {
      if (root.dataset.jpBound) return;
      root.dataset.jpBound = '1';
      root.classList.add('is-enhanced');
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const panels = [...root.querySelectorAll('[role="tabpanel"]')];
      panels.forEach((panel) => { if (!panel.hasAttribute('tabindex')) panel.tabIndex = 0; });
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const revealTab = (tab) => {
        const nav = tab.closest('[role="tablist"]');
        if (!nav || nav.scrollWidth <= nav.clientWidth) return;
        const left = tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2;
        nav.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
      };
      const select = (tab, focus = true) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
        revealTab(tab);
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1, Home: 0, End: 0 }[e.key];
          if (dir === undefined) return;
          e.preventDefault();
          if (e.key === 'Home') return select(tabs[0]);
          if (e.key === 'End') return select(tabs[tabs.length - 1]);
          select(tabs[(i + dir + tabs.length) % tabs.length]);
        });
      });
      const tabOwnerId = root.closest('[id^="shopify-section-"]')?.id;
      const findTab = (available, key) => available.find((tab) => tab.dataset.jpTab === key)
        || (key === 'specs' ? available.find((tab) => tab.dataset.jpTabModule === 'edge')
          || available.find((tab) => /^b\d+$/.test(tab.dataset.jpTab || '')) : null);
      // Review/rating and other deep links can survive a variant section swap.
      // Their one listener resolves the current connected tabs at activation.
      const openCurrentTab = (key) => {
        const owner = tabOwnerId ? document.getElementById(tabOwnerId) : document;
        const liveRoot = owner?.querySelector('[data-jp-ptabs]');
        if (!liveRoot?.isConnected) return false;
        const liveTab = findTab([...liveRoot.querySelectorAll('[role="tab"]')], key);
        if (!liveTab) return false;
        liveTab.click();
        liveRoot.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        return true;
      };
      const reviewsTab = tabs.find((t) => t.dataset.jpTab === 'reviews');
      if (reviewsTab) {
        if (window.location.hash === '#jp-reviews' || window.location.hash === '#jp-reviews-anchor') openCurrentTab('reviews');
        document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
          if (a.dataset.jpBound) return;
          a.dataset.jpBound = '1';
          a.addEventListener('click', (e) => { if (openCurrentTab('reviews')) e.preventDefault(); });
        });
      }

      /* generic deep-links: [data-jp-open-tab="<key>"] selects the matching tab (specs, overview…).
         Used by jp-spec-strip's "Full specs" link; only binds when that tab exists, else the href scroll wins. */
      document.querySelectorAll('[data-jp-open-tab]').forEach((a) => {
        if (a.dataset.jpBound) return;
        const key = a.getAttribute('data-jp-open-tab');
        /* Preserve the configured edge/flow fallback when specs is absent. */
        if (!findTab(tabs, key)) return;
        a.dataset.jpBound = '1';
        a.addEventListener('click', (e) => { if (openCurrentTab(key)) e.preventDefault(); });
      });
    });
    };
    initProductTabs();

    /* #jp-reviews target may be a standalone section (P4c.3) — smooth-scroll links */
    const reviewsSection = document.getElementById('jp-reviews');
    if (reviewsSection) {
      document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
        if (a.dataset.jpBound) return;
        a.dataset.jpBound = '1';
        a.addEventListener('click', (e) => {
          e.preventDefault();
          reviewsSection.scrollIntoView({ behavior: (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth', block: 'start' });
        });
      });
    }
  };

  /* Combined listings can replace <main> while this layout-owned controller
     remains active. The original instance owns Dawn's product-info lifecycle
     and initializes the newly inserted main. Retire old global pub/sub
     callbacks before binding the replacement. */
  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target;
    if (!productInfo?.matches?.('product-info[id^="MainProduct"]')) return;
    if (activeProductInfo && activeProductInfo !== productInfo) {
      activeProductCleanup?.();
      abortProductControllers();
      disconnectProductObservers();
    }
    activeProductInfo = productInfo;
    queueMicrotask(init);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const unloadingProduct = event.target?.matches?.('product-info[id^="MainProduct"]')
      ? event.target
      : event.target?.querySelector?.('product-info[id^="MainProduct"]');
    if (!unloadingProduct || (activeProductInfo && unloadingProduct !== activeProductInfo)) return;
    activeProductCleanup?.();
    abortProductControllers();
    disconnectProductObservers();
    activeProductInfo = null;
  });

  ready(init);
  document.addEventListener('shopify:section:load', () => init());
  document.addEventListener('jp:privacy:change', steerCard);
})();
